import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Flame, Snowflake, ArrowRight, CalendarBlank } from "@phosphor-icons/react";

const SERVICE_META = {
  sauna: { label: "Sauna", price: 25, accent: "hot", icon: <Flame size={22} weight="light" color="#BE3A34" />, desc: "60 min · ~90°C" },
  ice_bath: { label: "Banho de Gelo", price: 10, accent: "cold", icon: <Snowflake size={22} weight="light" color="#6BB9B8" />, desc: "60 min · ~4°C" },
  combo: { label: "Sauna + Banho de Gelo", price: 35, accent: "combo", icon: (
    <div className="flex gap-1"><Flame size={22} weight="light" color="#BE3A34" /><Snowflake size={22} weight="light" color="#6BB9B8" /></div>
  ), desc: "60 min · ciclo completo" },
};

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function initialDate() {
  const now = new Date();
  if (now.getHours() >= 19) now.setDate(now.getDate() + 1);
  return toISO(now);
}

function nextDays(n = 14) {
  const days = [];
  const today = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    days.push(d);
  }
  return days;
}
const DAY_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export default function Book() {
  const [params] = useSearchParams();
  const [service, setService] = useState("combo");
  const [date, setDate] = useState(initialDate);
  const [hour, setHour] = useState(null);
  const [avail, setAvail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const cancelled = params.get("cancelled");

  const days = useMemo(() => nextDays(14), []);

  useEffect(() => {
    let cancel = false;
    setHour(null);
    setLoading(true);
    api.get(`/availability?date=${date}`).then(({ data }) => {
      if (!cancel) setAvail(data);
    }).finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [date]);

  const isToday = date === toISO(new Date());
  const currentHour = new Date().getHours();

  const slotAvailableCount = (slot) => {
    if (service === "sauna") return slot.sauna_available;
    if (service === "ice_bath") return slot.ice_bath_available;
    return slot.combo_available;
  };

  const proceed = async () => {
    setErr("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings/checkout", {
        service_type: service,
        date,
        hour,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setErr(formatApiError(e));
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="book-page">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 space-y-14">
          <header className="fade-in">
            <p className="overline">— Reserva</p>
            <h1 className="font-display text-5xl md:text-6xl mt-3 tracking-tight text-[#003D4C]">
              Defina o seu <span className="text-[#003D4C]/55">ritual.</span>
            </h1>
            {cancelled && (
              <div className="mt-6 border border-[#BE3A34]/30 bg-[#BE3A34]/5 text-[#BE3A34] text-sm px-4 py-3" data-testid="cancel-banner">
                Pagamento cancelado. Pode escolher outro horário e tentar novamente.
              </div>
            )}
          </header>

          <section className="fade-in d-1">
            <p className="overline mb-4">01 · Serviço</p>
            <div className="grid md:grid-cols-3 gap-4">
              {Object.entries(SERVICE_META).map(([key, m]) => (
                <button key={key} type="button" onClick={() => setService(key)}
                  className={`service-card text-left ${service === key ? `selected ${m.accent}` : ""}`}
                  data-testid={`service-option-${key}`}>
                  <div className="flex items-start justify-between">
                    {m.icon}
                    <div className="font-display text-2xl text-[#003D4C]">€{m.price}</div>
                  </div>
                  <div className="font-display text-2xl mt-6 text-[#003D4C]">{m.label}</div>
                  <p className="text-xs text-[#003D4C]/60 mt-1">{m.desc}</p>
                </button>
              ))}
            </div>
          </section>

          <section className="fade-in d-2">
            <p className="overline mb-4">02 · Data</p>
            <div className="flex gap-2 overflow-x-auto scroll-x pb-2" data-testid="date-strip">
              {days.map((d) => {
                const iso = toISO(d);
                const selected = iso === date;
                return (
                  <button key={iso} onClick={() => setDate(iso)}
                    className={`min-w-[78px] py-3 px-2 border text-center transition-all ${
                      selected
                        ? "border-[#BE3A34] bg-[#BE3A34]/5 text-[#003D4C]"
                        : "hairline text-[#003D4C]/65 hover:text-[#003D4C] hover:border-[#003D4C]/35"
                    }`}
                    data-testid={`date-${iso}`}>
                    <div className="text-xs uppercase tracking-wider">{DAY_LABELS[d.getDay()]}</div>
                    <div className="font-display text-2xl mt-1 text-[#003D4C]">{d.getDate()}</div>
                    <div className="text-[10px] uppercase text-[#003D4C]/45">{d.toLocaleString("pt-PT", { month: "short" })}</div>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="fade-in d-3">
            <div className="flex items-baseline justify-between mb-4">
              <p className="overline">03 · Hora</p>
              <p className="text-xs text-[#003D4C]/55">Capacidade: 8 pessoas / hora</p>
            </div>
            {loading || !avail ? (
              <div className="text-[#003D4C]/55 text-sm" data-testid="slots-loading">A consultar disponibilidade…</div>
            ) : (
              <>
                {(() => {
                  const anyOpen = avail.slots.some((s) => {
                    const a = slotAvailableCount(s);
                    const past = isToday && s.hour <= currentHour;
                    return a > 0 && !past;
                  });
                  if (anyOpen) return null;
                  const tomorrow = new Date(date + "T00:00:00");
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  return (
                    <div className="border border-[#BE3A34]/30 bg-[#BE3A34]/5 px-5 py-4 mb-4 flex items-center justify-between flex-wrap gap-3" data-testid="no-slots-notice">
                      <div className="text-sm">
                        <div className="font-display text-xl text-[#003D4C]">Sem horários disponíveis nesta data.</div>
                        <div className="text-[#003D4C]/65 mt-1">
                          {isToday ? "As sessões de hoje já terminaram. Veja a partir de amanhã." : "Esta data está esgotada — escolha outra data acima."}
                        </div>
                      </div>
                      {isToday && (
                        <button onClick={() => setDate(toISO(tomorrow))} className="btn-hot px-5 py-2 text-sm" data-testid="jump-tomorrow-btn">
                          Ver amanhã
                        </button>
                      )}
                    </div>
                  );
                })()}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="slots-grid">
                  {avail.slots.map((s) => {
                    const available = slotAvailableCount(s);
                    const past = isToday && s.hour <= currentHour;
                    const full = available <= 0 || past;
                    const selected = hour === s.hour;
                    return (
                      <button key={s.hour} disabled={full} onClick={() => setHour(s.hour)}
                        className={`slot-card text-left ${selected ? "selected" : ""} ${full ? "full" : ""}`}
                        data-testid={`slot-${s.hour}`}>
                        <div className="font-display text-2xl text-[#003D4C]">{s.label}</div>
                        <div className="mt-2 flex justify-between items-end">
                          <span className="text-xs text-[#003D4C]/55">
                            {past ? "Já passou" : full ? "Esgotado" : `${available} lugares`}
                          </span>
                          <span className={`h-1.5 w-12 ${full ? "bg-[#003D4C]/15" : available > 4 ? "bg-[#6BB9B8]" : "bg-[#BE3A34]"}`} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </section>
        </div>

        <aside className="lg:col-span-4">
          <div className="card sticky top-24" data-testid="summary-card">
            <p className="overline">Resumo</p>
            <div className="font-display text-3xl mt-3 text-[#003D4C]">{SERVICE_META[service].label}</div>
            <p className="text-sm text-[#003D4C]/60 mt-1">{SERVICE_META[service].desc}</p>

            <div className="my-6 divide-y divide-[#003D4C]/8">
              <SummaryRow label="Data" value={
                <span className="inline-flex items-center gap-2">
                  <CalendarBlank size={14} weight="light" />
                  {new Date(date + "T00:00:00").toLocaleDateString("pt-PT", { weekday: "long", day: "numeric", month: "long" })}
                </span>
              } />
              <SummaryRow label="Hora" value={hour !== null ? `${String(hour).padStart(2, "0")}:00 – ${String(hour + 1).padStart(2, "0")}:00` : "—"} />
              <SummaryRow label="Sessão" value="60 min" />
            </div>

            <div className="flex justify-between items-baseline border-t hairline pt-4">
              <span className="text-sm text-[#003D4C]/65">Total</span>
              <span className="font-display text-4xl text-[#003D4C]" data-testid="summary-total">€{SERVICE_META[service].price}</span>
            </div>

            {err && <p className="text-sm text-[#BE3A34] mt-4" data-testid="book-error">{err}</p>}

            <button disabled={hour === null || submitting} onClick={proceed}
              className="btn-hot w-full py-3.5 mt-6 inline-flex items-center justify-center gap-2"
              data-testid="checkout-btn">
              {submitting ? "A redirecionar…" : <>Pagar e reservar <ArrowRight size={16} weight="light" /></>}
            </button>
            <p className="text-[11px] text-[#003D4C]/50 mt-3 text-center">Pagamento seguro via Stripe · Cancelamento até 24h antes.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between items-center py-3 text-sm">
      <span className="text-[#003D4C]/60">{label}</span>
      <span className="text-[#003D4C]">{value}</span>
    </div>
  );
}
